/**
 * Costurile unui container: marfa proprie, servicii facturate, deconturi.
 *
 * Aritmetica NU se face aici. Vine gata din API, unde e testata — altfel ar exista doua definitii
 * ale aceleiasi cifre, iar la prima schimbare ar divergea.
 */
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Link2, Plus, RefreshCw, Unlink } from 'lucide-react'
import { api } from '../../lib/api'
import { grupeazaMarfa, PRAG_MARFA_PLIATA, type RandMarfa } from '../../lib/marfa'
import {
  CautaFurnizor,
  mesajDupaCreare,
  type FurnizorAles,
  type FurnizorCreat,
} from './CautaFurnizor'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'

/** De ce acțiune de teren ține rândul. `null` = comun pe campanie, nerepartizat. */
interface PeActiune {
  actiuneId: number | null
  actiuneDenumire: string | null
}

interface RandFactura extends PeActiune {
  alocareId: number
  /** `spv` = din e-Factura; `externa` = furnizor care nu trece prin SPV. */
  sursa: 'spv' | 'externa'
  facturaId: number
  furnizor: string | null
  numar: string | null
  data: string
  moneda: string
  /** Baza facturii întregi, în lei, ca să se vadă când alocarea e parțială. */
  baza: number | null
  /** Cât s-a alocat aici — deja fără TVA, în lei. */
  cost: number
  nota: string | null
}

/** Factură de la un furnizor care nu trece prin SPV. */
interface CandidatExtern {
  id: number
  furnizor: string
  numar: string
  data: string
  moneda: string
  valoare: number
  baza: number
  ramas: number
  descriere: string | null
  legataAici: boolean
}

interface RandDecont extends PeActiune {
  lineId: number
  period: string
  angajat: string
  furnizor: string | null
  docDate: string | null
  cost: number
  bazaNecunoscuta: boolean
  sumeNeconcordante: boolean
}

interface RandAprobat {
  cerereId: number
  actiuneId: number | null
  actiuneDenumire: string | null
  sku: string
  denumire: string
  cantitate: number
  valoare: number | null
}

interface RandFaraBon {
  actiuneId: number
  actiuneDenumire: string
  nrPv: string
  sku: string
  denumire: string
  cantitate: number
  valoare: number | null
}

interface ConsumDirect extends PeActiune {
  id: number
  motiv: string
  nrDoc: string
  statusBon: string
  bonEaDocNo: string | null
  bonEroare: string | null
  stornat: boolean
  createdAt: string
  valoare: number
  linii: { sku: string; denumire: string; cantitate: number; pretUnitar: number | null }[]
}

interface Costuri {
  campanie: {
    id: number
    cod: string
    titlu: string
    bugetAlocat: number | null
    taxaParticipare: number | null
    taxaMoneda: string | null
  }
  marfa: {
    total: number
    randuri: RandMarfa[]
    valoareNecunoscuta: number
    consumDirect: ConsumDirect[]
    totalConsumDirect: number
  }
  /** Aprobată sau plecată pentru activitate, fără bon încă. Intră în total (de la aprobare). */
  marfaNeconsumata: {
    laAgenti: {
      total: number
      randuri: { sku: string; denumire: string; ramas: number; valoare: number | null }[]
    }
    faraBon: { total: number; randuri: RandFaraBon[] }
    /** Aprobată, încă netransferată (16.10.2026). Intră în total. */
    aprobata: { total: number; randuri: RandAprobat[] }
  }
  facturi: { total: number; randuri: RandFactura[] }
  deconturi: { total: number; randuri: RandDecont[]; detaliat: boolean }
  /** Cât ține de fiecare acțiune, și cât rămâne comun. Calculat pe server. */
  peActiune: { actiuneId: number; denumire: string; total: number }[]
  comun: number
  total: number
}

interface Candidat {
  id: number
  furnizor: string | null
  /** Codul emitentului, din e-factura. Cu el se creează furnizorul fără să se retasteze nimic. */
  cifFurnizor: string | null
  /** Id-ul din nomenclator, când emitentul e deja acolo. Gol = se poate adăuga. */
  furnizorId: number | null
  docId: string | null
  /** Data de pe factură; când lipsește din XML, data sosirii în SPV. */
  data: string
  dataCreare: string
  totalWithTax: number
  /** Totalul fără TVA — plafonul alocărilor. */
  baza: number | null
  alocatDeja: number
  ramas: number
  detalii: string | null
  legataAici: boolean
}

const lei = (n: number): string =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function eroare(err: unknown): string {
  const e = err as { response?: { data?: { error?: { message?: string }; message?: string } } }
  return e.response?.data?.error?.message ?? e.response?.data?.message ?? 'A eșuat.'
}

export function CosturiContainer({
  campanieId,
  canWrite,
}: {
  campanieId: number
  canWrite: boolean
}) {
  const qc = useQueryClient()
  const cheie = ['costuri', campanieId]
  /*
    TOTALUL SPONSORIZARILOR pe anul curent.

    Alexandru, 01.10.2026: „vreau afisat total sponsorizare/an curent". Cifra nu exista nicaieri —
    se putea afla doar adunand bonurile din EA, container cu container. Se arata pe orice ecran de
    costuri, nu doar pe cele de sponsorizare: tocmai cand emiti un bon vrei sa stii unde esti.

    Nu e o verificare de plafon si nu se poarta ca una — scutirea de TVA tine de 3‰ din cifra de
    afaceri, care nu e in CRM. O bara de progres aici ar parea exacta fara sa fie.
  */
  const sponsorizari = useQuery({
    queryKey: ['sponsorizari-an'],
    queryFn: async () =>
      (
        await api.get<{ an: number; total: number; containere: number }>(
          '/marketing/costuri/sponsorizari',
        )
      ).data,
  })
  const [legaDeschis, setLegaDeschis] = useState(false)
  /*
    Filtrele cautarii de facturi.

    Fara ele, fereastra e cea a containerului — ±10 zile fata de perioada lui. Buna cand
    evenimentul e recent, inutila cand a fost acum trei luni. Furnizorul e cel dupa care se cauta
    de fapt: omul stie de la cine asteapta factura.
  */
  const [consumDeschis, setConsumDeschis] = useState(false)
  const [facturaNoua, setFacturaNoua] = useState(false)
  /*
    Lista de marfa, desfacuta pe bonuri. Implicit e pliata cand sunt multe randuri: acelasi produs
    iese pe cate un bon separat, si se vede acelasi rand de zeci de ori (Alexandru, 18.09.2026).
  */
  const [marfaDetaliat, setMarfaDetaliat] = useState(false)
  const [furnizor, setFurnizor] = useState('')
  const [zile, setZile] = useState('')
  /*
    Pe ce acțiune te uiți. `null` = toate; `0` = doar ce e comun pe campanie.

    Filtrarea se face AICI, peste datele deja citite, nu printr-o cerere nouă: o campanie are zeci
    de rânduri, nu mii, iar un singur răspuns înseamnă că schimbarea selectorului e instantanee și
    că totalurile de sus nu pot divergea de liniile de dedesubt.
  */
  const [actiuneAleasa, setActiuneAleasa] = useState<number | null>(null)
  /** Factura pe care o împarți între acțiuni. `null` = fereastra e închisă. */
  const [deRepartizat, setDeRepartizat] = useState<RandFactura | null>(null)

  /*
    Mutarea unei linii pe o acțiune, sau înapoi la comun.

    O singură mutație pentru toate trei felurile — întrebarea e aceeași, „de ce vizită ține
    cheltuiala asta?", iar trei mutații aproape identice ar fi divergat la prima corectură.
  */
  /*
    STERGEREA unui consum direct, ca sa poata fi refacut.

    Alexandru, 30.09.2026: „acum o sa sterg bonul din ERP. Cum il stergem si din CRM, ca sa il
    refacem?" — nu se putea. Bonul iesise din gestiunea gresita, iar randul ramanea in CRM pe
    vecie, cu valoarea lui in costurile activitatii.

    Cere NUMARUL documentului din ExpertAccounts, tastat: nu stergem nimic acolo si nici nu putem,
    deci singura garantie ca n-a ramas document orfan e ca omul s-a uitat. De acolo il ia.
  */
  const stergeConsum = useMutation({
    mutationFn: async (v: { id: number; docEa: string | null }) =>
      api.delete(`/marketing/costuri/${campanieId}/consum/${v.id}`, {
        params: v.docEa ? { docEa: v.docEa } : {},
      }),
    onSuccess: () => {
      toast.success('Consum șters. Referința se eliberează, îl poți reface.')
      void qc.invalidateQueries({ queryKey: ['costuri', campanieId] })
      void qc.invalidateQueries({ queryKey: ['produse-transferate', campanieId] })
    },
    onError: (err) => toast.error(eroare(err)),
  })

  const mutaPeActiune = useMutation({
    mutationFn: async (v: {
      tip: 'alocari' | 'consum' | 'deconturi'
      id: number
      actiuneId: number | null
    }) => api.patch(`/marketing/costuri/${v.tip}/${v.id}/actiune`, { actiuneId: v.actiuneId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cheie })
    },
    onError: (e) => toast.error(eroare(e)),
  })

  const q = useQuery({
    queryKey: cheie,
    queryFn: async () =>
      (await api.get<{ costuri: Costuri }>(`/marketing/costuri/${campanieId}`)).data.costuri,
  })

  const candidati = useQuery({
    queryKey: ['costuri', campanieId, 'candidati', furnizor, zile],
    enabled: legaDeschis,
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (furnizor.trim()) params.furnizor = furnizor.trim()
      if (zile.trim()) params.zile = zile.trim()
      return (
        await api.get<{ facturi: Candidat[] }>(
          `/marketing/costuri/${campanieId}/candidati-facturi`,
          { params },
        )
      ).data.facturi
    },
  })

  const candidatiExterni = useQuery({
    queryKey: ['costuri', campanieId, 'candidati-externi', furnizor, zile],
    enabled: legaDeschis,
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (furnizor.trim()) params.furnizor = furnizor.trim()
      if (zile.trim()) params.zile = zile.trim()
      return (
        await api.get<{ facturi: CandidatExtern[] }>(
          `/marketing/costuri/${campanieId}/candidati-facturi-externe`,
          { params },
        )
      ).data.facturi
    },
  })

  const leagaExterna = useMutation({
    mutationFn: async (v: { facturaId: number; suma: number }) =>
      (
        await api.post(`/marketing/costuri/${campanieId}/facturi-externe/${v.facturaId}`, {
          suma: v.suma,
        })
      ).data,
    onSuccess: () => {
      toast.success('Factura a fost legată.')
      void qc.invalidateQueries({ queryKey: ['costuri', campanieId] })
    },
    onError: (err) => toast.error(eroare(err)),
  })

  const leaga = useMutation({
    mutationFn: async (v: { anafInboundId: number; suma: number }) =>
      (await api.post(`/marketing/costuri/${campanieId}/facturi`, v)).data,
    onSuccess: () => {
      toast.success('Factura a fost legată.')
      void qc.invalidateQueries({ queryKey: ['costuri', campanieId] })
    },
    onError: (err) => toast.error(eroare(err)),
  })

  /*
    Emitentul unei facturi din SPV, adaugat in nomenclatorul de furnizori.

    Datele vin din e-factura — numele SI codul fiscal —, deci nu se retasteaza si nu se cauta
    nimic: identitatea e cea de pe documentul pe care l-a emis chiar el.
  */
  const adaugaFurnizor = useMutation({
    mutationFn: async (v: { nume: string; cif: string | null }) =>
      (
        await api.post<{ furnizor: FurnizorCreat }>('/furnizori', {
          nume: v.nume,
          cif: v.cif,
          // România, explicit: aici sunt facturile din SPV, iar SPV e românesc. La facturile
          // externe țara se cere de la om, fiindcă acolo aproape niciodată nu e România.
          tara: 'Romania',
          sursa: 'crm',
        })
      ).data.furnizor,
    onSuccess: (f) => {
      const m = mesajDupaCreare(f)
      if (m.fel === 'ok') toast.success(m.text)
      else toast.warning(m.text, { duration: 12_000 })
      // Propunerile se recitesc: randul lui trece de la buton la „furnizor ✓".
      void qc.invalidateQueries({ queryKey: ['costuri', campanieId, 'candidati'] })
    },
    onError: (err) => toast.error(eroare(err)),
  })

  /*
    Aducerea valorii reale din EA. La cerere, nu automat: un apel per document, iar macheta trebuie
    sa se deschida instant.
  */
  const pretEa = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          rezultat: {
            documente: number
            documenteGasite: number
            randuriActualizate: number
            probleme: { docn: string; motiv: string }[]
          }
        }>(`/marketing/costuri/${campanieId}/pret-ea`)
      ).data.rezultat,
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['costuri', campanieId] })
      if (r.documente === 0) {
        toast.info('Nicio marfă ieșită pe bon pentru acest container.')
      } else if (r.randuriActualizate === 0) {
        // Zero randuri nu e o eroare, dar nici o reusita: se spune de ce, nu se tace.
        toast.error(
          r.probleme[0]?.motiv ?? 'ExpertAccounts n-a întors niciun cost pentru documentele astea.',
        )
      } else {
        const rest = r.probleme.length > 0 ? ` · ${r.probleme.length} documente n-au răspuns` : ''
        toast.success(
          `${r.randuriActualizate} rânduri confirmate din ${r.documenteGasite} documente${rest}`,
        )
      }
    },
    onError: (err) => toast.error(eroare(err)),
  })

  const dezleaga = useMutation({
    mutationFn: async (alocareId: number) => api.delete(`/marketing/costuri/facturi/${alocareId}`),
    onSuccess: () => {
      toast.success('Factura a fost dezlegată.')
      void qc.invalidateQueries({ queryKey: ['costuri', campanieId] })
    },
    onError: (err) => toast.error(eroare(err)),
  })

  if (q.isLoading) return <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
  if (q.isError || !q.data)
    return <p className="text-sm text-b4l-red">Costurile nu s-au putut citi.</p>

  const tot = q.data
  /** `0` înseamnă „comun": rândurile fără acțiune. */
  const tine = (r: PeActiune) =>
    actiuneAleasa == null ||
    (actiuneAleasa === 0 ? r.actiuneId == null : r.actiuneId === actiuneAleasa)
  const c: Costuri = actiuneAleasa == null ? tot : filtreaza(tot, tine)
  const buget = c.campanie.bugetAlocat
  const procent = buget && buget > 0 ? Math.round((c.total / buget) * 100) : null
  const ramas = buget == null ? null : Math.round((buget - c.total) * 100) / 100

  return (
    <div className="space-y-4">
      {/* Antetul: bugetul, cheltuitul, rămasul. */}
      <div className="rounded-lg border border-b4l-border p-3">
        <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
          <Cifra eticheta="Buget" valoare={buget} />
          <Cifra eticheta="Cheltuit" valoare={c.total} accent />
          <Cifra eticheta="Rămas" valoare={ramas} rosuSubZero />
          {/*
            Taxa agreată e REPER, nu cost: costul realizat vine din factura organizatorului. Fără
            distincția asta, aceeași sumă ar fi numărată de două ori.
          */}
          {c.campanie.taxaParticipare != null && (
            <div>
              <div className="text-xs text-b4l-text-muted">Taxă agreată</div>
              <div className="tabular-nums text-b4l-text-muted">
                {lei(c.campanie.taxaParticipare)} {c.campanie.taxaMoneda ?? ''}
              </div>
              <div className="text-[10px] text-b4l-text-dim">reper, nu se adună</div>
            </div>
          )}
        </div>
        {procent != null && (
          <div className="mt-2">
            <div className="h-2 overflow-hidden rounded bg-b4l-bg">
              <div
                className={`h-full ${procent > 100 ? 'bg-b4l-red' : 'bg-b4l-green'}`}
                style={{ width: `${Math.min(100, procent)}%` }}
              />
            </div>
            <div className="mt-1 text-xs text-b4l-text-muted">{procent}% din buget</div>
          </div>
        )}
      </div>

      {/*
        Defalcarea pe acțiuni de teren.

        Apare doar când campania CHIAR are acțiuni: la un eveniment singur, un rând „Comun” lângă
        un total identic n-ar spune nimic și ar cere o citire în plus la fiecare deschidere.

        Cifrele vin de la server, din aceleași rânduri care se arată dedesubt — ecranul doar le
        așază. Rândurile sunt butoane: apeși pe o acțiune și listele de mai jos se strâng la ea.
      */}
      {tot.peActiune.length > 0 && (
        <div className="rounded-lg border border-b4l-border">
          <div className="flex items-center justify-between border-b border-b4l-border px-3 py-2">
            <h3 className="text-sm font-semibold">Pe acțiuni de teren</h3>
            {actiuneAleasa != null && (
              <button
                type="button"
                onClick={() => setActiuneAleasa(null)}
                className="text-xs text-b4l-blue-dark hover:underline"
              >
                Vezi tot
              </button>
            )}
          </div>
          <ul>
            {[
              ...tot.peActiune.map((a) => ({ id: a.actiuneId, nume: a.denumire, total: a.total })),
              { id: 0, nume: 'Comun pe campanie', total: tot.comun },
            ].map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => setActiuneAleasa(actiuneAleasa === r.id ? null : r.id)}
                  className={`flex w-full items-center gap-3 border-b border-b4l-border px-3 py-2 text-left text-sm last:border-0 ${
                    actiuneAleasa === r.id ? 'bg-b4l-green-light' : 'hover:bg-b4l-bg'
                  }`}
                >
                  <span className={`flex-1 ${r.id === 0 ? 'text-b4l-text-muted' : ''}`}>
                    {r.nume}
                  </span>
                  <span className="tabular-nums">{lei(r.total)}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-3 border-t border-b4l-border px-3 py-2 text-sm font-semibold">
            <span className="flex-1">Total campanie</span>
            <span className="tabular-nums">{lei(tot.total)}</span>
          </div>
        </div>
      )}

      {sponsorizari.data && sponsorizari.data.total > 0 && (
        <p className="text-xs text-b4l-text-muted">
          Sponsorizări în bunuri, {sponsorizari.data.an}:{' '}
          <strong className="text-b4l-text">{lei(sponsorizari.data.total)}</strong> pe{' '}
          {sponsorizari.data.containere}{' '}
          {sponsorizari.data.containere === 1 ? 'activitate' : 'activități'}. Doar bonurile plecate
          în ExpertAccounts, la prețul de la emitere.
        </p>
      )}

      <Sectiune
        titlu="Marfă proprie"
        total={c.marfa.total}
        actiune={
          canWrite &&
          c.marfa.randuri.length > 0 && (
            <Button
              variant="secondary"
              onClick={() => pretEa.mutate()}
              loading={pretEa.isPending}
              title="Citește din ExpertAccounts cât a fost înregistrat efectiv pentru marfa ieșită"
            >
              <RefreshCw size={14} /> Confirmă din EA
            </Button>
          )
        }
      >
        {c.marfa.randuri.length === 0 ? (
          <Gol>Nicio marfă ieșită pe bon încă.</Gol>
        ) : !marfaDetaliat && c.marfa.randuri.length > PRAG_MARFA_PLIATA ? (
          /*
            Pliat: totalul pe PRODUS, nu lista de bonuri.

            Nu e doar o ascundere — e chiar informatia pe care o cauta cineva in lista lunga:
            cate bucati au iesit si cat au costat, nu pe ce bon anume.
          */
          <>
            <table className="w-full text-sm">
              <tbody>
                {grupeazaMarfa(c.marfa.randuri).map((g) => (
                  <tr
                    key={g.sku || g.denumire}
                    className="border-b border-b4l-border last:border-0"
                  >
                    <td className="py-1.5 pr-3 font-mono text-[11px] text-b4l-text-muted">
                      {g.sku}
                    </td>
                    <td className="py-1.5 pr-3">{g.denumire}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-b4l-text-muted">
                      {g.cantitate} buc
                      <span className="ml-1 text-[11px] text-b4l-text-dim">
                        · {g.bonuri} {g.bonuri === 1 ? 'bon' : 'bonuri'}
                      </span>
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {lei(g.valoare)}
                      {g.estimat && (
                        <span
                          className="ml-1 text-[10px] text-b4l-text-dim"
                          title="Preț de la emitere, neconfirmat din ExpertAccounts"
                        >
                          ~
                        </span>
                      )}
                      {g.faraPret > 0 && (
                        <span
                          className="ml-1 text-[10px] text-b4l-amber-dark"
                          title={`${g.faraPret} rânduri fără preț, neincluse`}
                        >
                          !
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              onClick={() => setMarfaDetaliat(true)}
              className="mt-1 text-xs text-b4l-blue hover:underline"
            >
              Arată cele {c.marfa.randuri.length} rânduri, pe bonuri
            </button>
          </>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {c.marfa.randuri.map((r, i) => (
                <tr
                  key={`${r.bonDocNo}-${r.sku}-${i}`}
                  className="border-b border-b4l-border last:border-0"
                >
                  <td className="py-1.5 pr-3 font-mono text-[11px] text-b4l-text-muted">
                    {r.bonDocNo}
                  </td>
                  <td className="py-1.5 pr-3">{r.denumire}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-b4l-text-muted">
                    {r.cantitate} ×{' '}
                    {r.pret == null ? <span className="text-b4l-amber-dark">?</span> : lei(r.pret)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {r.valoare == null ? '—' : lei(r.valoare)}
                    {r.estimat && (
                      <span
                        className="ml-1 text-[10px] text-b4l-text-dim"
                        title="Preț de la emitere, neconfirmat din ExpertAccounts"
                      >
                        ~
                      </span>
                    )}
                    {r.diferentaMare && (
                      <span
                        className="ml-1 text-[10px] text-b4l-amber-dark"
                        title="Prețul confirmat diferă mult de cel estimat"
                      >
                        !
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {marfaDetaliat && c.marfa.randuri.length > PRAG_MARFA_PLIATA && (
          <button
            type="button"
            onClick={() => setMarfaDetaliat(false)}
            className="mt-1 text-xs text-b4l-blue hover:underline"
          >
            Adună la loc, pe produse
          </button>
        )}
        {c.marfa.valoareNecunoscuta > 0 && (
          <p className="mt-1 text-xs text-b4l-amber-dark">
            {c.marfa.valoareNecunoscuta} rânduri fără preț — nu intră în total.
          </p>
        )}

        {/*
          Consumul direct: marfa care a ieșit din gestiune pentru eveniment fără să ajungă la un
          contact — tricouri de voluntari, mostre de protocol, materiale distruse la montaj.
          Aceeași secțiune ca marfa din predări, fiindcă pentru cine citește e tot marfă proprie;
          separată doar cât să se vadă de unde vine.
        */}
        <div className="mt-3 border-t border-b4l-border pt-2">
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-b4l-text-muted">
              Consum direct
              {c.marfa.totalConsumDirect > 0 && (
                <span className="ml-2 tabular-nums">{lei(c.marfa.totalConsumDirect)}</span>
              )}
            </span>
            {canWrite && (
              <Button
                variant="secondary"
                className="!px-2 !py-0.5 !text-xs"
                onClick={() => setConsumDeschis((v) => !v)}
              >
                <Plus size={13} /> {consumDeschis ? 'Renunță' : 'Adaugă'}
              </Button>
            )}
          </div>

          {c.marfa.consumDirect.length === 0 && !consumDeschis && (
            <p className="text-xs text-b4l-text-muted">
              Nimic încă. Aici intră marfa care iese pentru eveniment fără să fie predată cuiva.
            </p>
          )}

          {c.marfa.consumDirect.map((cd) => (
            <div key={cd.id} className="flex items-baseline gap-2 py-1 text-sm">
              <span className="font-mono text-[11px] text-b4l-text-dim">{cd.nrDoc}</span>
              <span className="min-w-0 flex-1 truncate">
                {cd.motiv}
                <span className="ml-2 text-xs text-b4l-text-muted">
                  {cd.linii.map((l) => `${l.denumire} ×${l.cantitate}`).join(', ')}
                </span>
              </span>
              {/*
                REUSITA SE VEDE, cu numarul documentului din ExpertAccounts.

                Pana acum randul tacea cand bonul plecase: se arata doar esecul. Alexandru,
                30.09.2026: „bonul de consum nu a ajuns in ExpertAccounts CD-EVT-2026-0014-001" —
                ajunsese, ca documentul MK1069, dar ecranul nu spunea nici ca a plecat, nici sub
                ce nume, iar el il cauta in EA dupa referinta NOASTRA.

                Referinta si numarul documentului sunt doua lucruri diferite: primul e cheia
                noastra impotriva dublarii, al doilea e ce scrie in ERP. Cine cauta acolo are
                nevoie de al doilea. Transferurile il arata de mult, sub forma „Trimis · vt14".
              */}
              {cd.statusBon === 'trimis' ? (
                <span
                  className="shrink-0 rounded-full bg-b4l-green-light px-2 py-0.5 text-[11px] text-b4l-green-dark"
                  title="Documentul din ExpertAccounts. Caută-l acolo după numărul ăsta, nu după referința noastră."
                >
                  Trimis{cd.bonEaDocNo ? ` · ${cd.bonEaDocNo}` : ''}
                </span>
              ) : (
                <span
                  className="shrink-0 text-[11px] text-b4l-red"
                  title={cd.bonEroare ?? undefined}
                >
                  {cd.statusBon === 'esuat' ? 'respins de EA' : cd.statusBon}
                </span>
              )}
              {cd.stornat && (
                <span className="shrink-0 text-[11px] text-b4l-text-dim">stornat</span>
              )}
              {canWrite && (
                <button
                  disabled={stergeConsum.isPending}
                  onClick={() => {
                    if (!cd.bonEaDocNo) {
                      if (confirm(`Ștergi ${cd.nrDoc}? N-a produs niciun document în EA.`))
                        stergeConsum.mutate({ id: cd.id, docEa: null })
                      return
                    }
                    const raspuns = window.prompt(
                      [
                        `${cd.nrDoc} a produs documentul ${cd.bonEaDocNo} în ExpertAccounts.`,
                        '',
                        'Ștergerea de aici NU îl șterge și acolo. Șterge-l întâi în EA, altfel',
                        'rămâne document care scoate marfa din gestiune, fără urmă în CRM.',
                        '',
                        `Ca să confirmi că l-ai șters, scrie numărul: ${cd.bonEaDocNo}`,
                      ].join('\n'),
                      '',
                    )
                    if (raspuns === null) return
                    stergeConsum.mutate({ id: cd.id, docEa: raspuns.trim() })
                  }}
                  className="shrink-0 text-[11px] text-b4l-red hover:underline disabled:opacity-40"
                  title="Șterge din evidența CRM. Documentul din EA trebuie șters acolo."
                >
                  Șterge
                </button>
              )}
              {/* Consumul direct nu e tabel, deci nu poate primi o celulă: același selector, gol. */}
              {tot.peActiune.length > 0 &&
                (canWrite ? (
                  <select
                    value={cd.actiuneId ?? ''}
                    disabled={mutaPeActiune.isPending}
                    onChange={(e) =>
                      mutaPeActiune.mutate({
                        tip: 'consum',
                        id: cd.id,
                        actiuneId: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    className="max-w-[11rem] shrink-0 rounded border border-b4l-border bg-transparent px-1 py-0.5 text-xs text-b4l-text-muted disabled:opacity-50"
                    title="De ce acțiune de teren ține consumul ăsta"
                  >
                    <option value="">Comun</option>
                    {tot.peActiune.map((a) => (
                      <option key={a.actiuneId} value={a.actiuneId}>
                        {a.denumire}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="shrink-0 text-xs text-b4l-text-muted">
                    {cd.actiuneDenumire ?? 'Comun'}
                  </span>
                ))}
              <span className="shrink-0 tabular-nums">{lei(cd.valoare)}</span>
            </div>
          ))}

          {consumDeschis && (
            <FormularConsum
              campanieId={campanieId}
              onGata={() => {
                setConsumDeschis(false)
                void qc.invalidateQueries({ queryKey: ['costuri', campanieId] })
              }}
            />
          )}
        </div>

        <MarfaNeconsumata date={c.marfaNeconsumata} />
      </Sectiune>

      {deRepartizat && (
        <ModalRepartizare
          rand={deRepartizat}
          actiuni={tot.peActiune}
          onClose={() => setDeRepartizat(null)}
          onGata={() => {
            setDeRepartizat(null)
            void qc.invalidateQueries({ queryKey: cheie })
          }}
        />
      )}

      <Sectiune
        titlu="Servicii (SPV)"
        total={c.facturi.total}
        actiune={
          canWrite && (
            <Button variant="secondary" onClick={() => setLegaDeschis((v) => !v)}>
              <Link2 size={14} /> {legaDeschis ? 'Ascunde propunerile' : 'Leagă factură'}
            </Button>
          )
        }
      >
        {c.facturi.randuri.length === 0 ? (
          <Gol>Nicio factură legată.</Gol>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {c.facturi.randuri.map((f) => (
                <tr key={f.alocareId} className="border-b border-b4l-border last:border-0">
                  <td className="py-1.5 pr-3 text-xs text-b4l-text-muted">{f.data}</td>
                  <td className="py-1.5 pr-3">
                    {f.furnizor ?? '—'}
                    {f.numar && (
                      <span className="ml-2 font-mono text-[11px] text-b4l-text-dim">
                        {f.numar}
                      </span>
                    )}
                    {/* Sursa se vede: cele externe n-au TVA și nu apar în SPV. */}
                    {f.sursa === 'externa' && (
                      <span
                        className="ml-2 rounded bg-b4l-bg px-1.5 text-[10px] text-b4l-text-muted"
                        title="Furnizor care nu trece prin SPV — taxare inversă, fără TVA pe factură"
                      >
                        externă{f.moneda !== 'RON' ? ` · ${f.moneda}` : ''}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-right text-xs text-b4l-text-muted">
                    {/* „din" apare doar la alocare parțială: altfel repetă aceeași cifră. */}
                    {f.baza != null && f.cost < f.baza - 0.005
                      ? `din ${lei(f.baza)} fără TVA`
                      : 'toată factura'}
                  </td>
                  <CelulaActiune
                    rand={f}
                    actiuni={tot.peActiune}
                    canWrite={canWrite}
                    seSchimba={mutaPeActiune.isPending}
                    onSchimba={(actiuneId) =>
                      mutaPeActiune.mutate({ tip: 'alocari', id: f.alocareId, actiuneId })
                    }
                  />
                  <td className="py-1.5 text-right tabular-nums">{lei(f.cost)}</td>
                  <td className="py-1.5 pl-2">
                    {/*
                      Repartizarea are rost doar pe rândul COMUN: unul deja pus pe o acțiune se
                      mută cu selectorul de alături, nu se mai sparge.
                    */}
                    {canWrite && tot.peActiune.length > 0 && f.actiuneId == null && (
                      <button
                        type="button"
                        onClick={() => setDeRepartizat(f)}
                        className="mr-2 text-xs text-b4l-blue-dark hover:underline"
                        title="Împarte suma între acțiunile de teren"
                      >
                        Repartizează
                      </button>
                    )}
                    {canWrite && (
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm('Dezlegi factura de acest container?'))
                            dezleaga.mutate(f.alocareId)
                        }}
                        className="text-b4l-text-dim hover:text-b4l-red"
                        title="Dezleagă"
                      >
                        <Unlink size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {legaDeschis && (
          <div className="mt-3 rounded border border-b4l-border bg-b4l-bg-soft p-2">
            <div className="mb-2 flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
                Furnizor
                <input
                  value={furnizor}
                  onChange={(e) => setFurnizor(e.target.value)}
                  placeholder="Romexpo, Print Expert…"
                  className="w-56 rounded border border-b4l-border px-2 py-1 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
                Ultimele … zile
                <input
                  value={zile}
                  onChange={(e) => setZile(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  placeholder="fără limită"
                  className="w-40 rounded border border-b4l-border px-2 py-1 text-sm"
                />
              </label>
              {(furnizor || zile) && (
                <button
                  type="button"
                  onClick={() => {
                    setFurnizor('')
                    setZile('')
                  }}
                  className="pb-1 text-xs text-b4l-text-muted underline"
                >
                  șterge filtrele
                </button>
              )}
            </div>
            {/*
              Textul spune DUPA CE se caută acum, fiindcă regula nu mai e una singură: numărul de
              zile bate tot, furnizorul scris desface fereastra, iar fără niciunul rămâne perioada
              containerului. Un text fix ar minți în două cazuri din trei.
            */}
            <p className="mb-2 text-xs text-b4l-text-dim">
              {zile.trim()
                ? `Facturi din ultimele ${zile.trim()} zile`
                : furnizor.trim()
                  ? `Toate facturile de la „${furnizor.trim()}", indiferent de dată`
                  : 'Facturi cu data în ±10 zile față de perioada containerului'}
              , în RON, nealocate integral. Suma propusă e cât a mai rămas liber — se poate schimba.
            </p>

            {candidati.isLoading && <p className="text-sm text-b4l-text-muted">Se caută…</p>}
            {candidati.data?.length === 0 && (
              <Gol>
                {furnizor.trim()
                  ? `Nicio factură de la „${furnizor.trim()}" care să nu fie deja alocată integral.`
                  : 'Nicio factură potrivită. Scrie furnizorul — atunci se caută în tot istoricul.'}
              </Gol>
            )}

            {(candidati.data?.length ?? 0) > 0 && (
              /*
                Lista, nu tabel.

                Tabelul cu sapte coloane nu incapea in fereastra: numele furnizorului se rupea pe
                trei randuri, iar butonul „Leaga" iesea in afara si cerea derulare laterala. O
                actiune la care nu se ajunge fara scroll nu e o actiune.

                Doua randuri per factura: identificarea sus, continutul dedesubt. Incape la orice
                latime, iar butonul ramane mereu la vedere.
              */
              <div className="divide-y divide-b4l-border">
                {(candidati.data ?? []).map((f) => (
                  <div key={f.id} className="flex items-start gap-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
                        <span className="whitespace-nowrap text-xs text-b4l-text-muted">
                          {f.data}
                        </span>
                        {f.docId && (
                          <span className="font-mono text-[11px] text-b4l-text-dim">{f.docId}</span>
                        )}
                        <span className="font-medium">{f.furnizor ?? '—'}</span>
                        {/*
                          Emitentul facturii, ca furnizor în nomenclator.

                          E-factura dă numele ȘI codul fiscal, deci identitatea e sigură — mai
                          sigură decât de la ANAF, fiindcă vine chiar de pe documentul emis de el.
                          Un click, fără nimic de retastat.
                        */}
                        {f.furnizor &&
                          (f.furnizorId != null ? (
                            <span
                              className="rounded bg-emerald-100 px-1.5 text-[10px] text-emerald-700"
                              title="Furnizorul e în nomenclator"
                            >
                              furnizor ✓
                            </span>
                          ) : (
                            <button
                              type="button"
                              disabled={adaugaFurnizor.isPending}
                              onClick={() =>
                                adaugaFurnizor.mutate({
                                  nume: f.furnizor as string,
                                  cif: f.cifFurnizor,
                                })
                              }
                              className="rounded bg-b4l-bg px-1.5 text-[10px] text-b4l-blue hover:underline disabled:opacity-50"
                              title="Adaugă emitentul în nomenclatorul de furnizori și trimite-l în ExpertAccounts"
                            >
                              + furnizor
                            </button>
                          ))}
                        {f.legataAici && (
                          <span className="rounded bg-b4l-bg px-1.5 text-[10px] text-b4l-green-dark">
                            legată aici
                          </span>
                        )}
                      </div>
                      {f.detalii && (
                        <div
                          className="mt-0.5 truncate text-xs text-b4l-text-muted"
                          title={f.detalii}
                        >
                          {f.detalii}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="tabular-nums text-sm">
                        {f.baza != null ? lei(f.baza) : lei(f.totalWithTax)}
                        <span className="ml-1 text-[10px] text-b4l-text-dim">fără TVA</span>
                      </div>
                      {/* „Liber" se arata doar cand difera: altfel repeta valoarea degeaba. */}
                      {f.baza != null && f.ramas !== f.baza && (
                        <div className="text-[11px] tabular-nums text-b4l-text-muted">
                          liber {lei(f.ramas)}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="secondary"
                      className="!px-2 !py-0.5 !text-xs shrink-0"
                      disabled={leaga.isPending}
                      onClick={() => {
                        /*
                          Se cere suma FĂRĂ TVA, citită de pe factură. Prima variantă cerea suma
                          brută și deducea baza pro-rata — greșit la linii cu cote diferite, și
                          inutil, fiindcă numărul e scris pe factură.
                        */
                        const s = window.prompt(
                          `Cât din factura de la ${f.furnizor ?? 'furnizor'} ține de acest container?

Suma FĂRĂ TVA. Liber: ${lei(f.ramas)} lei.`,
                          String(f.ramas),
                        )
                        if (!s) return
                        const suma = Number(s.replace(',', '.'))
                        if (!Number.isFinite(suma) || suma <= 0) return
                        leaga.mutate({ anafInboundId: f.id, suma })
                      }}
                    >
                      Leagă
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {/*
              Facturile externe, sub cele din SPV. Aceleasi filtre — furnizor si cate zile in urma
              —, ca sa se caute la fel in amandoua listele.
            */}
            <div className="mt-3 border-t border-b4l-border pt-2">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-b4l-text-muted">
                  Facturi externe
                  <span className="ml-2 font-normal text-b4l-text-dim">
                    furnizori care nu trec prin SPV
                  </span>
                </span>
                {canWrite && (
                  <Button
                    variant="secondary"
                    className="!px-2 !py-0.5 !text-xs"
                    onClick={() => setFacturaNoua((v) => !v)}
                  >
                    <Plus size={13} /> {facturaNoua ? 'Renunță' : 'Înregistrează'}
                  </Button>
                )}
              </div>

              {facturaNoua && (
                <FormularFacturaExterna
                  onGata={() => {
                    setFacturaNoua(false)
                    void qc.invalidateQueries({ queryKey: ['costuri', campanieId] })
                  }}
                />
              )}

              {candidatiExterni.data?.length === 0 && !facturaNoua && (
                <p className="text-xs text-b4l-text-muted">
                  Nicio factură externă în interval. Google, Meta, TikTok și celelalte nu ajung în
                  SPV — se înregistrează aici.
                </p>
              )}

              <div className="divide-y divide-b4l-border">
                {(candidatiExterni.data ?? []).map((f) => (
                  <div key={f.id} className="flex items-start gap-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                        <span className="whitespace-nowrap text-xs text-b4l-text-muted">
                          {f.data}
                        </span>
                        <span className="font-mono text-[11px] text-b4l-text-dim">{f.numar}</span>
                        <span className="font-medium">{f.furnizor}</span>
                        {f.legataAici && (
                          <span className="rounded bg-b4l-bg px-1.5 text-[10px] text-b4l-green-dark">
                            legată aici
                          </span>
                        )}
                      </div>
                      {f.descriere && (
                        <div className="mt-0.5 truncate text-xs text-b4l-text-muted">
                          {f.descriere}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="tabular-nums text-sm">{lei(f.baza)}</div>
                      {f.moneda !== 'RON' && (
                        <div className="text-[11px] tabular-nums text-b4l-text-dim">
                          {f.valoare.toFixed(2)} {f.moneda}
                        </div>
                      )}
                      {f.ramas !== f.baza && (
                        <div className="text-[11px] tabular-nums text-b4l-text-muted">
                          liber {lei(f.ramas)}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="secondary"
                      className="!px-2 !py-0.5 !text-xs shrink-0"
                      disabled={leagaExterna.isPending}
                      onClick={() => {
                        const s = window.prompt(
                          `Cât din factura de la ${f.furnizor} ține de acest container?\n\nFactura n-are TVA (taxare inversă). Liber: ${lei(f.ramas)} lei.`,
                          String(f.ramas),
                        )
                        if (!s) return
                        const suma = Number(s.replace(',', '.'))
                        if (!Number.isFinite(suma) || suma <= 0) return
                        leagaExterna.mutate({ facturaId: f.id, suma })
                      }}
                    >
                      Leagă
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Sectiune>

      <Sectiune titlu="Deconturi" total={c.deconturi.total}>
        {!c.deconturi.detaliat ? (
          <p className="text-xs text-b4l-text-muted">
            Totalul e inclus, dar bonurile angajaților se văd doar cu drept de deconturi. Legarea
            lor se face din contabilitate.
          </p>
        ) : c.deconturi.randuri.length === 0 ? (
          <Gol>Nicio linie de decont legată.</Gol>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {c.deconturi.randuri.map((d) => (
                <tr key={d.lineId} className="border-b border-b4l-border last:border-0">
                  <td className="py-1.5 pr-3 text-xs text-b4l-text-muted">
                    {d.docDate ?? d.period}
                  </td>
                  <td className="py-1.5 pr-3">{d.furnizor ?? '—'}</td>
                  <td className="py-1.5 pr-3 text-xs text-b4l-text-muted">{d.angajat}</td>
                  <CelulaActiune
                    rand={d}
                    actiuni={tot.peActiune}
                    canWrite={canWrite}
                    seSchimba={mutaPeActiune.isPending}
                    onSchimba={(actiuneId) =>
                      mutaPeActiune.mutate({ tip: 'deconturi', id: d.lineId, actiuneId })
                    }
                  />
                  <td className="py-1.5 text-right tabular-nums">
                    {lei(d.cost)}
                    {d.bazaNecunoscuta && (
                      <span
                        className="ml-1 text-[10px] text-b4l-amber-dark"
                        title="Baza nu se cunoaște — costul e o limită superioară"
                      >
                        !
                      </span>
                    )}
                    {d.sumeNeconcordante && (
                      <span
                        className="ml-1 text-[10px] text-b4l-red"
                        title="Sumele de pe bon nu se închid"
                      >
                        ⚠
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Sectiune>
    </div>
  )
}

/**
 * Aceleași date, dar numai rândurile unei acțiuni — și cu TOTALURILE refăcute din ele.
 *
 * Fără recalculare, secțiunile ar arăta cifra întregii campanii deasupra unei liste filtrate:
 * două numere care nu se potrivesc, pe același ecran, fără nimic care să explice de ce.
 */
function filtreaza(tot: Costuri, tine: (r: PeActiune) => boolean): Costuri {
  const marfa = tot.marfa.randuri.filter(tine)
  const consum = tot.marfa.consumDirect.filter(tine)
  const facturi = tot.facturi.randuri.filter(tine)
  const deconturi = tot.deconturi.randuri.filter(tine)
  const suma = (n: (number | null)[]) =>
    Math.round(n.reduce<number>((s, x) => s + (x ?? 0), 0) * 100) / 100

  const totalConsumDirect = suma(consum.map((x) => x.valoare))
  /*
    Ce poartă agenții e „comun": marfa se transferă unui om, nu unei acțiuni. Rămâne doar în
    vederea „Comun pe campanie", ca acolo totalul să se închidă cu cel de pe server.
  */
  const laAgenti = tine({ actiuneId: null, actiuneDenumire: null })
    ? tot.marfaNeconsumata.laAgenti
    : { total: 0, randuri: [] }
  const faraBon = tot.marfaNeconsumata.faraBon.randuri.filter(tine)
  const totalFaraBon = suma(faraBon.map((x) => x.valoare))
  // Aprobată, netransferată: la acțiunea cererii; fără acțiune, în „Comun pe campanie".
  const aprobata = tot.marfaNeconsumata.aprobata.randuri.filter(tine)
  const totalAprobata = suma(aprobata.map((x) => x.valoare))
  const totalMarfa =
    Math.round(
      (suma(marfa.map((x) => x.valoare)) +
        totalConsumDirect +
        laAgenti.total +
        totalFaraBon +
        totalAprobata) *
        100,
    ) / 100
  const totalFacturi = suma(facturi.map((x) => x.cost))
  const totalDeconturi = suma(deconturi.map((x) => x.cost))

  return {
    ...tot,
    marfa: {
      ...tot.marfa,
      randuri: marfa,
      consumDirect: consum,
      total: totalMarfa,
      totalConsumDirect,
      valoareNecunoscuta: marfa.filter((x) => x.valoare == null).length,
    },
    marfaNeconsumata: {
      laAgenti,
      faraBon: { total: totalFaraBon, randuri: faraBon },
      aprobata: { total: totalAprobata, randuri: aprobata },
    },
    facturi: { total: totalFacturi, randuri: facturi },
    deconturi: { ...tot.deconturi, total: totalDeconturi, randuri: deconturi },
    total: Math.round((totalMarfa + totalFacturi + totalDeconturi) * 100) / 100,
  }
}

/**
 * Celula „Pe" — de ce acțiune ține linia, și cu ce se poate schimba.
 *
 * Un `select`, nu o fereastră: mutarea unei linii e o corectură măruntă, făcută de cineva care se
 * uită la listă, iar un dialog pentru fiecare rând ar transforma zece corecturi într-o corvoadă.
 *
 * Fără drept de scriere rămâne text: cine doar citește n-are de ce să vadă un câmp care nu face
 * nimic.
 */
/**
 * Împarte o factură între acțiunile de teren.
 *
 * O fereastră, nu un câmp pe rând: aici se scriu mai multe sume deodată și trebuie să se vadă cât
 * a mai rămas pe măsură ce le pui. Restul nedistribuit rămâne comun — asta e chiar întrebarea pe
 * care o pune ecranul, și de aceea cifra stă mereu sub ochi.
 */
function ModalRepartizare({
  rand,
  actiuni,
  onClose,
  onGata,
}: {
  rand: RandFactura
  actiuni: { actiuneId: number; denumire: string }[]
  onClose: () => void
  onGata: () => void
}) {
  const [sume, setSume] = useState<Record<number, string>>({})
  const numar = (v: string) => {
    const n = Number(v.replace(',', '.'))
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0
  }
  const parti = actiuni
    .map((a) => ({ actiuneId: a.actiuneId, suma: numar(sume[a.actiuneId] ?? '') }))
    .filter((p) => p.suma > 0)
  const repartizat = Math.round(parti.reduce((s, p) => s + p.suma, 0) * 100) / 100
  const ramas = Math.round((rand.cost - repartizat) * 100) / 100
  const preaMult = ramas < -0.004

  const trimite = useMutation({
    mutationFn: async () =>
      api.post(`/marketing/costuri/alocari/${rand.alocareId}/repartizeaza`, { parti }),
    onSuccess: () => {
      toast.success(
        ramas > 0.004
          ? `Repartizat ${lei(repartizat)}. Restul de ${lei(ramas)} rămâne comun.`
          : 'Factura e repartizată integral pe acțiuni.',
      )
      onGata()
    },
    onError: (e) => toast.error(eroare(e)),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={`Repartizează ${rand.furnizor ?? 'factura'}`}
      inchideLaClicInAfara={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Renunță
          </Button>
          <Button
            onClick={() => trimite.mutate()}
            loading={trimite.isPending}
            disabled={parti.length === 0 || preaMult}
          >
            Repartizează
          </Button>
        </>
      }
    >
      <p className="text-sm text-b4l-text-muted">
        Linia are <strong className="text-b4l-text">{lei(rand.cost)}</strong>. Scrie cât ține de
        fiecare acțiune; ce nu repartizezi rămâne comun pe campanie.
      </p>
      <ul className="mt-3 space-y-2">
        {actiuni.map((a) => (
          <li key={a.actiuneId} className="flex items-center gap-3">
            <span className="min-w-0 flex-1 truncate text-sm">{a.denumire}</span>
            <input
              inputMode="decimal"
              value={sume[a.actiuneId] ?? ''}
              onChange={(e) => setSume((v) => ({ ...v, [a.actiuneId]: e.target.value }))}
              placeholder="0"
              className="w-28 rounded border border-b4l-border px-2 py-1 text-right text-sm tabular-nums"
            />
          </li>
        ))}
      </ul>
      <div
        className={`mt-3 rounded-lg p-3 text-sm ${
          preaMult ? 'bg-b4l-red-light text-b4l-red-dark' : 'bg-b4l-bg'
        }`}
      >
        {preaMult ? (
          <>
            Ai repartizat {lei(repartizat)}, cu {lei(-ramas)} mai mult decât are linia.
          </>
        ) : (
          <>
            Repartizat {lei(repartizat)} · rămâne comun{' '}
            <strong className="tabular-nums">{lei(ramas)}</strong>
          </>
        )}
      </div>
    </Modal>
  )
}

function CelulaActiune({
  rand,
  actiuni,
  canWrite,
  seSchimba,
  onSchimba,
}: {
  rand: PeActiune
  actiuni: { actiuneId: number; denumire: string }[]
  canWrite: boolean
  seSchimba: boolean
  onSchimba: (actiuneId: number | null) => void
}) {
  if (actiuni.length === 0) return null
  if (!canWrite) {
    return (
      <td className="py-1.5 pr-3 text-xs text-b4l-text-muted">{rand.actiuneDenumire ?? 'Comun'}</td>
    )
  }
  return (
    <td className="py-1.5 pr-3">
      <select
        value={rand.actiuneId ?? ''}
        disabled={seSchimba}
        onChange={(e) => onSchimba(e.target.value === '' ? null : Number(e.target.value))}
        className="max-w-[11rem] rounded border border-b4l-border bg-transparent px-1 py-0.5 text-xs text-b4l-text-muted disabled:opacity-50"
        title="De ce acțiune de teren ține cheltuiala asta"
      >
        <option value="">Comun</option>
        {actiuni.map((a) => (
          <option key={a.actiuneId} value={a.actiuneId}>
            {a.denumire}
          </option>
        ))}
      </select>
    </td>
  )
}

function Cifra({
  eticheta,
  valoare,
  accent,
  rosuSubZero,
}: {
  eticheta: string
  valoare: number | null
  accent?: boolean
  rosuSubZero?: boolean
}) {
  return (
    <div>
      <div className="text-xs text-b4l-text-muted">{eticheta}</div>
      <div
        className={`tabular-nums ${accent ? 'text-base font-semibold' : ''} ${
          rosuSubZero && valoare != null && valoare < 0 ? 'text-b4l-red' : ''
        }`}
      >
        {valoare == null ? '—' : lei(valoare)}
      </div>
    </div>
  )
}

/**
 * Marfa plecată pentru activitate fără bon de consum încă (Madalin, 15.10.2026). INTRĂ în total:
 * cheltuiala cu marfa începe de la transfer, nu de la bon.
 *
 * În vederea pe o acțiune lipsește partea „la agenți": marfa se transferă unui om, nu unei
 * acțiuni — aceleași 30 de bucăți trec prin cinci farmacii.
 */
function MarfaNeconsumata({ date }: { date: Costuri['marfaNeconsumata'] }) {
  const { laAgenti, faraBon, aprobata } = date
  if (
    laAgenti.randuri.length === 0 &&
    faraBon.randuri.length === 0 &&
    aprobata.randuri.length === 0
  )
    return null
  // Doar restul pozitiv intra in subtotal; unul negativ fara pret nu-l face „cel putin".
  const faraPret = laAgenti.randuri.some((r) => r.ramas > 0 && r.valoare == null)
  const aprobataFaraPret = aprobata.randuri.some((r) => r.valoare == null)
  return (
    <div className="mt-3 border-t border-b4l-border pt-3 text-sm">
      <h4 className="font-medium">
        Fără bon încă
        <span className="ml-2 text-xs font-normal text-b4l-text-muted">
          intră în total de la aprobare; transferul și bonul nu mai schimbă cifra
        </span>
      </h4>
      {aprobata.randuri.length > 0 && (
        <div className="mt-2">
          <p className="text-b4l-text">
            Aprobată, netransferată încă:{' '}
            <strong className="tabular-nums">
              {lei(aprobata.total)}
              {aprobataFaraPret ? ' (cel puțin)' : ''}
            </strong>
          </p>
          <ul className="mt-1 grid gap-0.5 text-xs text-b4l-text-muted">
            {aprobata.randuri.map((r) => (
              <li key={`${r.cerereId}-${r.sku}`} className="flex justify-between gap-3">
                <span className="min-w-0 truncate">
                  {r.denumire} <span className="text-b4l-text-dim">{r.sku}</span> · {r.cantitate}{' '}
                  buc{r.actiuneDenumire ? ` · ${r.actiuneDenumire}` : ''}
                </span>
                <span className="shrink-0 tabular-nums">
                  {r.valoare == null ? 'fără preț' : lei(r.valoare)}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-b4l-text-dim">
            Restul cererilor aprobate din fila Marfă, la prețul de la aprobare (intrarea din
            ExpertAccounts, altfel costul de producție). La transfer trece la „Transferată”.
          </p>
        </div>
      )}
      {laAgenti.randuri.length > 0 && (
        <div className="mt-2">
          <p className="text-b4l-text">
            Transferată, nedată încă:{' '}
            <strong className="tabular-nums">
              {lei(laAgenti.total)}
              {faraPret ? ' (cel puțin)' : ''}
            </strong>
          </p>
          <ul className="mt-1 grid gap-0.5 text-xs text-b4l-text-muted">
            {laAgenti.randuri.map((r) => (
              <li key={r.sku} className="flex justify-between gap-3">
                <span className="min-w-0 truncate">
                  {r.denumire} <span className="text-b4l-text-dim">{r.sku}</span> · {r.ramas} buc
                  {r.ramas < 0 && (
                    <span className="text-b4l-amber-dark">
                      {' '}
                      — dat mai mult decât s-a transferat; lipsește un document
                    </span>
                  )}
                </span>
                <span className="shrink-0 tabular-nums">
                  {r.valoare == null ? 'fără preț' : lei(r.valoare)}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-b4l-text-dim">
            Transferat minus predat și consumat, din documentele CRM — nu e stocul citit din
            ExpertAccounts.
          </p>
        </div>
      )}
      {faraBon.randuri.length > 0 && (
        <div className="mt-2">
          <p className="text-b4l-text">
            Predată, bonul n-a ieșit încă:{' '}
            <strong className="tabular-nums">{lei(faraBon.total)}</strong>
          </p>
          <ul className="mt-1 grid gap-0.5 text-xs text-b4l-text-muted">
            {faraBon.randuri.map((r, i) => (
              <li key={`${r.nrPv}-${r.sku}-${i}`} className="flex justify-between gap-3">
                <span className="min-w-0 truncate">
                  {r.denumire} · {r.cantitate} buc · {r.actiuneDenumire} · PV {r.nrPv}
                </span>
                <span className="shrink-0 tabular-nums">
                  {r.valoare == null ? 'fără preț' : lei(r.valoare)}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-b4l-text-dim">
            Bonul se emite sau se reia din fila Marfă → Predări.
          </p>
        </div>
      )}
    </div>
  )
}

function Sectiune({
  titlu,
  total,
  actiune,
  children,
}: {
  titlu: string
  total: number
  actiune?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-b4l-border p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {titlu}
          <span className="ml-2 tabular-nums text-b4l-text-muted">{lei(total)}</span>
        </h3>
        {actiune}
      </div>
      {children}
    </div>
  )
}

function Gol({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-b4l-text-muted">{children}</p>
}

/**
 * Formularul de consum direct.
 *
 * Emite un document in CONTABILITATE, deci confirma inainte si spune limpede ce se intampla. La
 * refuzul EA nu se arunca: motivul se arata, iar randul ramane in lista cu starea lui.
 */
function FormularConsum({ campanieId, onGata }: { campanieId: number; onGata: () => void }) {
  const [motiv, setMotiv] = useState('')
  const [cautare, setCautare] = useState('')
  const [alese, setAlese] = useState<Record<number, number>>({})
  /*
    Data la care a IESIT marfa, nu cea la care se apasă butonul.

    Alexandru, 29.09.2026: „bonul de consum vreau să fie cu data de atunci, pentru că de fapt
    atunci a fost scos din gestiune, doar că abia acum se înregistrează". La Sense4Fit marfa
    plecase în august, iar bonul se emite în septembrie.
  */
  const [data, setData] = useState(() => new Date().toISOString().slice(0, 10))
  const azi = new Date().toISOString().slice(0, 10)

  /*
    PRODUSELE CARE AU IESIT DIN DEPOZIT pentru activitatea asta, nu tot catalogul.

    Alexandru, 30.09.2026: „aș vrea să apară doar produsele transferate pentru acest eveniment
    (...) cu cantitatea totală completată". Pana acum formularul cauta printre o mie de produse
    pentru un eveniment care primise doua — iar omul cauta pe dinafara ceva ce sistemul stia deja.

    Cautarea in tot catalogul ramane, dar ca ABATERE, nu ca punct de plecare: se intampla sa
    consumi ceva ce n-a trecut prin transfer — o tiparitura, un material de protocol. Nu inchidem
    usa aia, doar n-o mai punem in fata.
  */
  const transferate = useQuery({
    queryKey: ['produse-transferate', campanieId],
    queryFn: async () =>
      (
        await api.get<{
          produse: {
            productId: number
            sku: string
            denumire: string
            transferat: number
            ramas: number
          }[]
        }>(`/marketing/costuri/${campanieId}/produse-transferate`)
      ).data.produse,
  })
  /* Se cauta in catalog doar cand omul chiar cauta. Altfel, lista e cea a activitatii. */
  const [cautaInCatalog, setCautaInCatalog] = useState(false)

  /*
    DIN CE GESTIUNE IESE MARFA.

    Nu exista deloc pana pe 30.09.2026: bonul lua mereu gestiunea comuna din setari. Alexandru,
    vazand bonul emis din Sampling: „de ce? sigur era selectata gestiunea lui Dorian" — nu era de
    unde, formularul n-avea campul. Marfa lui plecase in gestiunea lui, iar bonul a scos-o din
    alta, adica din stocul altor activitati.

    Serverul spune ce propune — gestiunea in care au ajuns transferurile activitatii —, ecranul
    doar arata. O a doua deductie aici ar fi o a doua regula, si s-ar desparti de prima.
  */
  const gestiune = useQuery({
    queryKey: ['gestiune-consum', campanieId],
    queryFn: async () =>
      (
        await api.get<{
          propusa: number | null
          dinTransferuri: boolean
          aleActivitatii: number[]
          gestiuni: { id: number; nume: string }[]
        }>(`/marketing/costuri/${campanieId}/gestiune-consum`)
      ).data,
  })
  const [locId, setLocId] = useState<string>('')
  const gestiunePusa = useRef(false)
  useEffect(() => {
    if (gestiunePusa.current || !gestiune.data?.propusa) return
    gestiunePusa.current = true
    setLocId(String(gestiune.data.propusa))
  }, [gestiune.data])

  const produse = useQuery({
    queryKey: ['produse-consum', cautare],
    enabled: cautaInCatalog,
    queryFn: async () =>
      (
        await api.get<{ products: { id: number; sku: string; name: string }[] }>('/products', {
          params: { q: cautare || undefined, pageSize: 20 },
        })
      ).data.products,
  })

  /*
    CANTITATEA, completata din start cu ce a RAMAS nedat, nu cu ce s-a transferat.

    Alexandru cere „cantitatea totală completată", si la un eveniment din care n-a plecat nimic
    cele doua sunt egale. Cand s-a predat deja o parte, insa, totalul ar scoate din gestiune mai
    mult decat a intrat — si bonul ar fi fals. `ramas` e chiar cifra pe care o poti justifica.

    Se pune o SINGURA DATA, la incarcarea listei: o cifra schimbata de om nu se rescrie sub mana.
  */
  const prefacut = useRef(false)
  useEffect(() => {
    if (prefacut.current || !transferate.data) return
    prefacut.current = true
    const initial: Record<number, number> = {}
    for (const p of transferate.data) if (p.ramas > 0) initial[p.productId] = p.ramas
    if (Object.keys(initial).length > 0) setAlese(initial)
  }, [transferate.data])

  const emite = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          rezultat: {
            trimis: boolean
            nrDoc: string
            docNo?: string | null
            motiv: string | null
          }
        }>(`/marketing/costuri/${campanieId}/consum`, {
          motiv: motiv.trim(),
          data,
          /* Din ce gestiune iese marfa. Gol = cea propusa de server. */
          locId: locId ? Number(locId) : undefined,
          linii: Object.entries(alese)
            .filter(([, c]) => c > 0)
            .map(([id, cantitate]) => ({ productId: Number(id), cantitate })),
        })
      ).data.rezultat,
    onSuccess: (r) => {
      /*
        Mesajul spune NUMARUL DIN EA, nu referinta noastra: acolo il cauta omul. `r.nrDoc` e
        referinta, si dupa ea nu se gaseste nimic in ERP.
      */
      if (r.trimis)
        toast.success(
          r.docNo
            ? `Bonul a plecat în ExpertAccounts, documentul ${r.docNo}.`
            : `Bonul ${r.nrDoc} a plecat în ExpertAccounts.`,
        )
      // Un refuz nu e o eroare de sistem: documentul există la noi, cu motivul pe el.
      else toast.error(r.motiv ?? 'Bonul n-a putut fi emis.')
      onGata()
    },
    onError: (err) => toast.error(eroare(err)),
  })

  const nrAlese = Object.values(alese).filter((c) => c > 0).length
  const gata = motiv.trim().length >= 3 && nrAlese > 0

  return (
    <div className="mt-2 rounded border border-b4l-border bg-b4l-bg-soft p-2">
      <div className="mb-2 flex flex-wrap gap-2">
        <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
          De ce iese marfa
          <input
            value={motiv}
            onChange={(e) => setMotiv(e.target.value)}
            placeholder="tricouri voluntari, mostre protocol…"
            className="w-64 rounded border border-b4l-border px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
          Când a ieșit marfa
          <input
            type="date"
            value={data}
            max={azi}
            onChange={(e) => setData(e.target.value)}
            className="rounded border border-b4l-border px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
          Din ce gestiune
          <select
            value={locId}
            onChange={(e) => setLocId(e.target.value)}
            className="w-56 rounded border border-b4l-border px-2 py-1 text-sm"
          >
            <option value="">— alege —</option>
            {(gestiune.data?.gestiuni ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.nume} ({g.id})
                {gestiune.data?.aleActivitatii.includes(g.id) ? ' · a primit marfă' : ''}
              </option>
            ))}
          </select>
        </label>
        {cautaInCatalog ? (
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Caută în tot catalogul
            <input
              value={cautare}
              onChange={(e) => setCautare(e.target.value)}
              placeholder="denumire sau SKU"
              autoFocus
              className="w-56 rounded border border-b4l-border px-2 py-1 text-sm"
            />
          </label>
        ) : (
          <button
            type="button"
            onClick={() => setCautaInCatalog(true)}
            className="self-end pb-1 text-xs text-b4l-blue hover:underline"
          >
            Caută în tot catalogul
          </button>
        )}
      </div>

      {gestiune.data && !gestiune.data.dinTransferuri && locId && (
        <p className="mb-2 text-xs text-b4l-amber-dark">
          Gestiunea nu s-a putut deduce din transferurile activității (niciunul, sau mai multe
          destinații). E propusă cea comună din Setări — verifică dacă marfa chiar de acolo iese.
        </p>
      )}

      {data !== azi && (
        <p className="mb-2 text-xs text-b4l-amber-dark">
          Bonul se trimite în ExpertAccounts cu data de {data.split('-').reverse().join('.')}. Dacă
          luna e închisă acolo, ExpertAccounts îl refuză și afișează motivul.
        </p>
      )}

      {!cautaInCatalog && (transferate.data ?? []).length === 0 && !transferate.isLoading && (
        <p className="mb-2 text-xs text-b4l-text-muted">
          Nu a ieșit nicio marfă din depozit pentru activitatea asta. Caută în catalog dacă totuși
          consumi ceva.
        </p>
      )}

      <div className="max-h-48 space-y-1 overflow-y-auto">
        {!cautaInCatalog &&
          (transferate.data ?? []).map((p) => (
            <div key={p.productId} className="flex items-center gap-2 text-sm">
              <span className="w-24 shrink-0 font-mono text-[11px] text-b4l-text-dim">{p.sku}</span>
              <span className="min-w-0 flex-1 truncate">{p.denumire}</span>
              {/* Ce a ieșit și ce a mai rămas: cifra completată nu trebuie crezută pe cuvânt. */}
              <span
                className="shrink-0 text-[11px] text-b4l-text-muted"
                title={`Transferat ${p.transferat} buc pentru activitate; nedat încă ${p.ramas}`}
              >
                {p.ramas} din {p.transferat}
              </span>
              <input
                type="number"
                min={0}
                max={p.transferat}
                value={alese[p.productId] ?? ''}
                onChange={(e) =>
                  setAlese((a) => ({
                    ...a,
                    [p.productId]: Math.max(0, Number(e.target.value) || 0),
                  }))
                }
                className="w-20 rounded border border-b4l-border px-2 py-0.5 text-sm"
                placeholder="buc"
              />
            </div>
          ))}
        {cautaInCatalog &&
          (produse.data ?? []).map((p) => (
            <div key={p.id} className="flex items-center gap-2 text-sm">
              <span className="w-24 shrink-0 font-mono text-[11px] text-b4l-text-dim">{p.sku}</span>
              <span className="min-w-0 flex-1 truncate">{p.name}</span>
              <input
                type="number"
                min={0}
                value={alese[p.id] ?? ''}
                onChange={(e) =>
                  setAlese((a) => ({ ...a, [p.id]: Math.max(0, Number(e.target.value) || 0) }))
                }
                className="w-20 rounded border border-b4l-border px-2 py-0.5 text-sm"
                placeholder="buc"
              />
            </div>
          ))}
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-xs text-b4l-text-muted">
          {nrAlese === 0 ? 'Alege cel puțin un produs.' : `${nrAlese} produse alese`}
        </span>
        <Button
          disabled={!gata}
          loading={emite.isPending}
          onClick={() => {
            if (
              confirm(
                'Se emite un bon de consum în ExpertAccounts și marfa iese din gestiune. Continui?',
              )
            )
              emite.mutate()
          }}
        >
          Emite bonul
        </Button>
      </div>
    </div>
  )
}

/**
 * Inregistrarea unei facturi de la un furnizor care nu trece prin SPV.
 *
 * Sase campuri, cum s-a cerut. Cursul se ia DE PE FACTURA, nu se calculeaza din BNR: asa apare si
 * in jurnalul contabil, iar diferenta de curs de la plata ramane pe `6651` si nu e cost de
 * eveniment.
 *
 * Fara camp de TVA, si nu din uitare: serviciile intracomunitare si extracomunitare vin cu taxare
 * inversa — factura nu poarta TVA.
 */
function FormularFacturaExterna({ onGata }: { onGata: () => void }) {
  const [furnizor, setFurnizor] = useState<FurnizorAles | null>(null)
  const [numar, setNumar] = useState('')
  const [data, setData] = useState('')
  const [moneda, setMoneda] = useState('EUR')
  const [valoare, setValoare] = useState('')
  const [curs, setCurs] = useState('')
  const [descriere, setDescriere] = useState('')

  const salveaza = useMutation({
    mutationFn: async () =>
      (
        await api.post('/marketing/costuri/facturi-externe', {
          // Numele pleaca si ca text: pe el sta cheia impotriva dublei introduceri, si tot el e
          // ce scrie pe hartie. Legatura adauga identitatea, nu inlocuieste textul.
          furnizor: furnizor?.nume ?? '',
          furnizorId: furnizor?.id ?? null,
          numar: numar.trim(),
          data,
          moneda,
          valoare: Number(valoare.replace(',', '.')),
          curs: moneda === 'RON' ? 1 : Number(curs.replace(',', '.')),
          descriere: descriere.trim() || undefined,
        })
      ).data,
    onSuccess: () => {
      toast.success('Factura a fost înregistrată.')
      onGata()
    },
    onError: (err) => toast.error(eroare(err)),
  })

  const val = Number(valoare.replace(',', '.'))
  const c = moneda === 'RON' ? 1 : Number(curs.replace(',', '.'))
  const inLei = Number.isFinite(val) && Number.isFinite(c) && val > 0 && c > 0 ? val * c : null
  const gata = furnizor != null && numar.trim().length >= 1 && data !== '' && inLei != null

  return (
    <div className="mb-2 rounded border border-b4l-border bg-b4l-bg-soft p-2">
      <div className="flex flex-wrap gap-2">
        <Camp eticheta="Furnizor" latime="w-64">
          <CautaFurnizor value={furnizor} onChange={setFurnizor} />
        </Camp>
        <Camp eticheta="Număr" latime="w-32">
          <input
            value={numar}
            onChange={(e) => setNumar(e.target.value)}
            className="w-full rounded border border-b4l-border px-2 py-1 text-sm"
          />
        </Camp>
        <Camp eticheta="Data" latime="w-36">
          <input
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
            className="w-full rounded border border-b4l-border px-2 py-1 text-sm"
          />
        </Camp>
        <Camp eticheta="Monedă" latime="w-20">
          <select
            value={moneda}
            onChange={(e) => setMoneda(e.target.value)}
            className="w-full rounded border border-b4l-border px-2 py-1 text-sm"
          >
            {['EUR', 'USD', 'RON', 'GBP'].map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Camp>
        <Camp eticheta="Valoare" latime="w-28">
          <input
            value={valoare}
            onChange={(e) => setValoare(e.target.value)}
            placeholder="300"
            className="w-full rounded border border-b4l-border px-2 py-1 text-sm"
          />
        </Camp>
        {moneda !== 'RON' && (
          <Camp eticheta="Curs de pe factură" latime="w-32">
            <input
              value={curs}
              onChange={(e) => setCurs(e.target.value)}
              placeholder="5.0988"
              className="w-full rounded border border-b4l-border px-2 py-1 text-sm"
            />
          </Camp>
        )}
        <Camp eticheta="Ce s-a cumpărat" latime="w-56">
          <input
            value={descriere}
            onChange={(e) => setDescriere(e.target.value)}
            placeholder="GOOGLE*ADS1945983235"
            className="w-full rounded border border-b4l-border px-2 py-1 text-sm"
          />
        </Camp>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-xs text-b4l-text-muted">
          {inLei != null ? (
            <>
              <strong className="tabular-nums">{lei(Math.round(inLei * 100) / 100)}</strong> lei ·
              fără TVA, taxare inversă
            </>
          ) : (
            'Completează valoarea și cursul.'
          )}
        </span>
        <Button disabled={!gata} loading={salveaza.isPending} onClick={() => salveaza.mutate()}>
          Înregistrează
        </Button>
      </div>
    </div>
  )
}

function Camp({
  eticheta,
  latime,
  children,
}: {
  eticheta: string
  latime: string
  children: React.ReactNode
}) {
  return (
    <label className={`flex flex-col gap-1 text-xs text-b4l-text-muted ${latime}`}>
      {eticheta}
      {children}
    </label>
  )
}
